import React, {useEffect, useState} from 'react'
import {Field, FieldsManager} from '../../../data/services/fields'
import LocalStorage from '../../../util/localStorage'
import {useDispatch, useSelector} from 'react-redux'
import Resources from '../../../data/services/resources'
import {getSecondResource} from '../../../data/actions/secondResource'
import {download} from '../../../data/actions/download'
import {
    checkPerm,
    classNames,
    getDefaultTableOptions,
    getProp,
    saveTableColumns
} from '../../../common/util/util-helpers'
import {currentDate, toFrontDateNoTime} from '../../../common/util/util-dates'
import {READ_PERM} from '../../../common/util/util-consts'
import InventoryReportDialog from '../../../common/components/modal/inventory-report-dialog'
import moment from 'moment'
import Tippy from '@tippyjs/react'
import DocumentArrowDownIcon from '@heroicons/react/20/solid/DocumentArrowDownIcon'
import {ArchiveBoxXMarkIcon, InformationCircleIcon} from "@heroicons/react/24/outline"
import TableCard from "../../../common/components/resource-table/table-components/table-card"
import NoRecordsTable from "../../../common/components/no-records-found/no-records-table"
import TableFilters from "../../../common/components/resource-table/table-components/table-filters"
import ActiveFilters from "../../../common/components/resource-table/table-components/active-filters"
import Badge from "../../../common/components/badge"
import ResourceTable from "../../../common/components/resource-table"
import {LoaderSmall} from "../../../common/components/loader"
import {numberWithCommas} from "../../../util/util-formaters"
import {HelpList, ReportHelpPanel, SIGNAL_LABELS, VALUATION_NOTE, helpFor, withSortDisabled} from "./inventory-exceptions-help";
import Pagination from "../../../common/components/resource-table/table-components/pagination";
import TableCardFooter from "../../../common/components/resource-table/table-components/table-card-footer";

/**
 * The back catalogue: stock that has been sitting long enough to be a clean-up job rather
 * than something you can still chase.
 *
 * Two findings, on a months clock. A part is NEVER_SOLD when it has existed for at least
 * "Minimum age months" and has never been billed to anybody, and STALE when it used to
 * sell but nothing has gone out for "Stale months". Anything recent enough to still act on
 * belongs to the Inventory velocity report instead.
 *
 * Same endpoint as that report, with Mode=aging. End-user documentation lives in the
 * backend repo at docs/INV_TRACKING.md.
 */

const CATEGORY_LABELS = {
    NEVER_SOLD: 'Never sold',
    STALE: 'Stale'
}

const CATEGORY_BADGE_TYPE = {
    NEVER_SOLD: 'red',
    STALE: 'yellow'
}

// The values the endpoint's Category parameter accepts, which are not the row values above.
const CATEGORY_FILTER_OPTIONS = {
    'never-sold': 'Never sold',
    'stale': 'Stale'
}

const CAUSE_LABELS = {
    UNBILLED_WORK_ORDER: 'Unbilled work order',
    LIKELY_SHOP_SUPPLY: 'Likely shop supply',
    OBSOLETE: 'Obsolete',
    SLOW_MOVER: 'Slow mover'
}

const CAUSE_BADGE_TYPE = {
    UNBILLED_WORK_ORDER: 'red',
    LIKELY_SHOP_SUPPLY: 'orange',
    OBSOLETE: 'yellow',
    SLOW_MOVER: 'blue'
}

const SETTINGS_HELP = [
    ['Stale months', 'how long without a sale before stock that used to move counts as stale.'],
    ['Minimum age months', 'how long a part must have existed before "never sold" is a real finding rather than just new stock.'],
    ['Repeat purchases', 'how many separate bills count as buying something over and over. Reaching this without ever selling one is what marks a part as a likely shop supply.'],
    ['Min value on hand', 'hide rows below a dollar value. Starts at zero, because a cheap item bought twenty times and never sold is one of the clearest signals there is.']
]

const CAUSE_HELP = [
    ['Unbilled work order', 'it went on a truck and was never invoiced. Money you already spent and have not charged for. Still worth chasing even here.'],
    ['Likely shop supply', 'bought over and over across months and never sold once. That is a consumable wearing an inventory part\'s clothes; fix how it is classified and it stops inflating your stock forever.'],
    ['Obsolete', 'bought, never sold, and old enough that it is not going to sell. Write it off or return it.'],
    ['Slow mover', 'it does sell, just not lately. Decide whether to keep carrying it.']
]

export default function InventoryAgingReportTab({
                                                    translate,
                                                    pagePath
                                                }) {

    /** Store
     ================================================================= */
    const dispatch = useDispatch()
    const secondResource = useSelector((state) => state.secondResource)
    // The details dialog fetches its own data into dialogResource, and its invoice/expense
    // drill-down lands in thirdResource.
    const dialogResource = useSelector((state) => state.dialogResource)
    const thirdResource = useSelector((state) => state.thirdResource)
    const isLoading = secondResource.isLoading
    const data = secondResource?.data?.list ?? []
    const count = getProp(secondResource.data, 'count', 0)

    // Whole-population totals: they cover every aged part, not just this page, and they do
    // not narrow when the Category or Cause filter does. The breakdown is what you filter
    // *by*, so hiding it behind its own filter would be useless.
    const totals = secondResource?.data?.Totals ?? {}
    const totalValue = totals.Value ?? 0
    const byCause = totals.ByCause ?? {}
    const byCategory = totals.ByCategory ?? {}

    /** Helpers
     ================================================================= */

    const getFields = () => {
        // Headers for columns the endpoint cannot sort by are made non-clickable.
        return withSortDisabled({
            Category: new Field('Category', '', [''], false, 'custom', {
                render: (item) => {
                    const category = item.Category
                    if (!category) return '-'
                    return (
                        <Badge type={CATEGORY_BADGE_TYPE[category] ?? 'outline'}>
                            {CATEGORY_LABELS[category] ?? category}
                        </Badge>
                    )
                }
            }),
            LikelyCause: new Field('LikelyCause', '', [''], false, 'custom', {
                render: (item) => {
                    const cause = item.LikelyCause
                    if (!cause) return '-'
                    return (
                        <Badge type={CAUSE_BADGE_TYPE[cause] ?? 'outline'}>
                            {CAUSE_LABELS[cause] ?? cause}
                        </Badge>
                    )
                }
            }),
            ProductService: new Field('ProductService', '', [''], false, 'text', {}),
            SKU: new Field('SKU', '', [''], false, 'text', {}),
            OnHandQty: new Field('OnHandQty', '', [''], false, 'decimal', {
                render: (item) => numberWithCommas(item.OnHandQty ?? 0, 2)
            }),
            OnHandValue: new Field('OnHandValue', '', [''], false, 'decimal', {
                render: (item) => '$' + numberWithCommas(item.OnHandValue ?? 0, 2)
            }),
            UnitCost: new Field('UnitCost', '', [''], false, 'decimal', {
                render: (item) => item.UnitCost ? '$' + numberWithCommas(item.UnitCost, 2) : '-'
            }),
            DaysSinceLastSale: new Field('DaysSinceLastSale', '', [''], false, 'custom', {
                // The clock the stale test runs on. Shown in months because that is the
                // scale this report thinks in, with the raw days kept on hover.
                render: (item) => {
                    const days = item.DaysSinceLastSale
                    if (days === null || days === undefined) return <span className="text-red-600 font-semibold">Never sold</span>
                    const months = Math.floor(days / 30)
                    const colorClass = months >= 12 ? 'text-red-600' : months >= 6 ? 'text-yellow-600' : 'text-tm-gray-700'
                    return <span className={colorClass + ' font-semibold'} title={days + ' days'}>{months} mo</span>
                }
            }),
            LastSaleDate: new Field('LastSaleDate', '', [''], false, 'custom', {
                render: (item) => item.LastSaleDate ? toFrontDateNoTime(item.LastSaleDate) : 'Never'
            }),
            SellQty: new Field('SellQty', '', [''], false, 'decimal', {
                render: (item) => numberWithCommas(item.SellQty ?? 0, 2)
            }),
            DaysSinceLastPurchase: new Field('DaysSinceLastPurchase', '', [''], false, 'custom', {
                render: (item) => {
                    const days = item.DaysSinceLastPurchase
                    if (days === null || days === undefined) return '-'
                    return <span title={days + ' days'}>{Math.floor(days / 30)} mo</span>
                }
            }),
            LastPurchaseDate: new Field('LastPurchaseDate', '', [''], false, 'custom', {
                render: (item) => item.LastPurchaseDate ? toFrontDateNoTime(item.LastPurchaseDate) : '-'
            }),
            PurchaseEvents: new Field('PurchaseEvents', '', [''], false, 'custom', {
                render: (item) => item.PurchaseEvents ?? 0
            }),
            WorkOrderQty: new Field('WorkOrderQty', '', [''], false, 'decimal', {
                render: (item) => numberWithCommas(item.WorkOrderQty ?? 0, 2)
            }),
            WOQtyNeverBilled: new Field('WOQtyNeverBilled', '', [''], false, 'custom', {
                render: (item) => {
                    const qty = item.WOQtyNeverBilled ?? 0
                    if (!qty) return '-'
                    return <span className="text-red-600 font-semibold">{numberWithCommas(qty, 2)}</span>
                }
            }),
            DaysOfSupply: new Field('DaysOfSupply', '', [''], false, 'custom', {
                // Null means nothing has ever left, so there is no rate to project from.
                // That is a different statement to "a very large number of days".
                render: (item) => item.DaysOfSupply === null || item.DaysOfSupply === undefined
                    ? 'No sales'
                    : numberWithCommas(item.DaysOfSupply, 0)
            }),
            Signals: new Field('Signals', '', [''], false, 'custom', {
                // Plain text, not badges. A part can carry several signals, and wrapping
                // badges made the row grow to fit them, which broke the grid alignment.
                render: (item) => {
                    const signals = item.Signals ?? []
                    if (!signals.length) return '-'

                    const text = signals.map((s) => SIGNAL_LABELS[s] ?? s).join(', ')
                    return <span title={text}>{text}</span>
                }
            })
        })
    }

    const getQueryFields = () => {
        return {
            query: new Field('query', '', [''], false, 'search', {hideLabel: true, labelType: "float"}),
            // Labels resolve as field.<name> from src/assets/locale/en-wo.json, so no
            // explicit label metadata is needed.
            StaleMonths: new Field('StaleMonths', '6', [''], false, 'integer', {labelType: "float"}),
            MinAgeMonths: new Field('MinAgeMonths', '3', [''], false, 'integer', {labelType: "float"}),
            RepeatPurchaseThreshold: new Field('RepeatPurchaseThreshold', '3', [''], false, 'integer', {labelType: "float"}),
            MinOnHandValue: new Field('MinOnHandValue', '0', [''], false, 'integer', {labelType: "float"}),
            Category: new Field('Category', '', [''], false, 'select', {labelType: "float"}, {
                all: true,
                isClearable: true
            }),
            // Multi, for the same reason as the velocity report: the causes are a worklist,
            // not mutually exclusive choices.
            LikelyCause: new Field('LikelyCause', '', [''], false, 'multi-select', {labelType: "float"}, {
                isMulti: true,
                all: true
            }),
            offset: new Field('offset', '0', [''], false, 'text'),
            limit: new Field('limit', '100', [''], false, 'select', {hideLabel: true, labelType: "float"}, {
                menuPlacement: 'top',
                hasPortal: true
            })
        }
    }

    const getResourceName = () => {
        return Resources.InventoryExceptions
    }

    const getQuery = () => {
        return {
            ...FieldsManager.getFieldKeyValues(queryFilterFields),
            Mode: 'aging',
            sort: sort,
            sortBy: sortBy
        }
    }

    const getTableOptions = (pagePath, translate) => {
        const tablePageDefaults = {
            version: 1,
            behaviour: {
                rowSelect: false,
                canAdjustWidth: true,
                hasMenu: true
            },
            style: {
                condensed: true,
                isGPUAccelerated: true
            }
        }

        return getDefaultTableOptions(getFields(), tablePageDefaults, pagePath, translate)
    }

    /** State
     ================================================================= */
    const [queryFilterFields, setQueryFilterFields] = useState(getQueryFields())
    const [tableOptions, setTableOptions] = useState(getTableOptions(pagePath, translate))
    const [isTableFullScreen, setIsTableFullScreen] = useState(false)
    // Biggest money first. This is a clean-up list, so it is worked in value order rather
    // than by age -- and there is no Overdue column here to sort on.
    const [sort, setSort] = useState("DESC")
    const [sortBy, setSortBy] = useState("OnHandValue")
    const [isReportDialogOpen, setIsReportDialogOpen] = useState(false)
    const [selectedReportItem, setSelectedReportItem] = useState({})
    const [isHelpOpen, setIsHelpOpen] = useState(false)

    // Opening the movement ledger needs the parts report permission, which is a separate
    // grant to this report. Without it the dialog would open on an empty response, so the
    // row is simply not clickable.
    const canViewDetails = checkPerm(Resources.WorkOrderProductServicesReport, READ_PERM)

    // TableFilters resolves a select's options as selects[fieldName][value] -> label.
    const metadata = {
        Category: CATEGORY_FILTER_OPTIONS,
        LikelyCause: CAUSE_LABELS
    }

    /** Data events
     ================================================================= */
    const fetchData = () => {
        dispatch(getSecondResource({
            user: LocalStorage.get('user'),
            resource: getResourceName(),
            query: getQuery()
        }))
    }

    // Exports the filtered report rather than the page on screen: the server replaces
    // paging with its own export cap when format=EXCEL.
    const downloadExcel = () => {
        dispatch(download({
            user: LocalStorage.get('user'),
            resource: getResourceName(),
            query: Object.assign({
                format: 'EXCEL',
                name: 'inventory_aging_' + currentDate() + '.xlsx'
            }, getQuery())
        }))
    }

    /** Lifecycle
     ================================================================= */
    useEffect(() => {
        fetchData()
    }, [sort, sortBy, queryFilterFields])

    /** UI events
     ================================================================= */
    const handleFilterInputChange = (name, value) => {
        let queryFieldsUpdate = FieldsManager.updateField(queryFilterFields, name, value)

        // Any filter change invalidates the page you were on.
        if (name !== 'offset') {
            queryFieldsUpdate = FieldsManager.updateField(queryFieldsUpdate, 'offset', '0')
        }

        setQueryFilterFields(queryFieldsUpdate)
    }

    const handleClearFiltersClick = (excludeAdditional = []) => {
        const queryFilterFieldsTmp = Object.assign({}, queryFilterFields)
        // Clearing the thresholds to empty would silently fall back to the server defaults
        // and leave the inputs looking blank, so they are restored to their defaults.
        const defaultExcludedFields = ['limit', 'StaleMonths', 'MinAgeMonths', 'RepeatPurchaseThreshold', 'MinOnHandValue']
        const excludedFields = defaultExcludedFields.concat(excludeAdditional)

        Object.values(queryFilterFieldsTmp).filter((it) => !excludedFields.includes(it.name)).forEach((it) => {
            FieldsManager.updateField(queryFilterFieldsTmp, it.name, '')
        })

        FieldsManager.updateField(queryFilterFieldsTmp, 'StaleMonths', '6')
        FieldsManager.updateField(queryFilterFieldsTmp, 'MinAgeMonths', '3')
        FieldsManager.updateField(queryFilterFieldsTmp, 'RepeatPurchaseThreshold', '3')
        FieldsManager.updateField(queryFilterFieldsTmp, 'MinOnHandValue', '0')

        setQueryFilterFields(queryFilterFieldsTmp)
    }

    const handleFullScreenToggle = () => {
        setIsTableFullScreen(!isTableFullScreen)
    }

    const setOptions = (options) => {
        setTableOptions(options)
        saveTableColumns(pagePath, options)
    }

    const handleUpdateSort = (sortByTmp) => {
        setSort((sortByTmp === sortBy) ? (sort === "ASC" ? "DESC" : "ASC") : "DESC")
        setSortBy(sortByTmp)
    }

    const handleRowClick = (item) => {
        if (!canViewDetails || !item?.ProductServiceID) return

        setSelectedReportItem(item)
        setIsReportDialogOpen(true)
    }

    /** Render
     ================================================================= */
    return (
        <React.Fragment>
            <div className={"flex items-center mb-4"}>
                <div className="flex mx-4 gap-4 flex-wrap">
                    <Badge addClass={"relative"} type="outline">
                        {isLoading && (
                            <div className="px-5">
                                <LoaderSmall/>
                            </div>
                        )}

                        {!isLoading && (
                            <Tippy content={
                                <div className="text-xs p-1 max-w-sm">
                                    <p className="mb-2">Stock that has been sitting long enough to be a clean-up
                                        job rather than something you can still chase. Anything recent enough to
                                        act on is on the Inventory velocity report instead.</p>

                                    <p className="font-semibold mb-1">The settings</p>
                                    <HelpList items={SETTINGS_HELP} addClass="mb-2"/>

                                    <p className="font-semibold mb-1">What the causes mean</p>
                                    <HelpList items={CAUSE_HELP}/>

                                    <p className="mt-2">{VALUATION_NOTE}</p>
                                </div>
                            } allowHTML={true} maxWidth={400}>
                                <div className="flex items-center gap-1.5">
                                    <ArchiveBoxXMarkIcon className="w-5 h-5"/>
                                    <span>Aged parts:</span>
                                    <span className="font-bold">{totals.Products ?? 0}</span>
                                </div>
                            </Tippy>
                        )}
                    </Badge>

                    {!isLoading && !!(totals.Products ?? 0) && (
                        <>
                            <Badge type="outline">
                                <span>Value sitting: </span>
                                <span className="font-bold">${numberWithCommas(totalValue, 2)}</span>
                            </Badge>

                            {Object.keys(CATEGORY_LABELS)
                                .filter((category) => !!byCategory[category])
                                .map((category) => (
                                    <Badge key={category} type={CATEGORY_BADGE_TYPE[category]}>
                                        <span>{CATEGORY_LABELS[category]}: </span>
                                        <span className="font-bold">{byCategory[category].Products}</span>
                                        <span className="ml-1">(${numberWithCommas(byCategory[category].Value, 2)})</span>
                                    </Badge>
                                ))}

                            {Object.keys(CAUSE_LABELS)
                                .filter((cause) => !!byCause[cause])
                                .map((cause) => (
                                    <Badge key={cause} type="outline">
                                        <span>{CAUSE_LABELS[cause]}: </span>
                                        <span className="font-bold">{byCause[cause].Products}</span>
                                        <span className="ml-1">(${numberWithCommas(byCause[cause].Value, 2)})</span>
                                    </Badge>
                                ))}
                        </>
                    )}

                    <Tippy content={isHelpOpen ? 'Hide explanation' : 'How this report works'}>
                        <button
                            className="btn-icon"
                            aria-expanded={isHelpOpen}
                            onClick={() => setIsHelpOpen(!isHelpOpen)}
                        >
                            <InformationCircleIcon className={
                                classNames("w-5 h-5", isHelpOpen ? "text-primary" : "text-tm-gray-500")
                            }/>
                        </button>
                    </Tippy>
                </div>
            </div>

            {isHelpOpen && (
                <ReportHelpPanel
                    title="How this report works"
                    intro={(
                        <>Two findings, both on a months clock. <span className="font-medium">Never sold</span> is
                            a part old enough to judge that has never been billed to anybody.
                            <span className="font-medium"> Stale</span> is a part that used to sell and then
                            stopped, with stock still on the shelf. Anything recent enough to still chase is on
                            the Inventory velocity report instead.</>
                    )}
                    settings={SETTINGS_HELP}
                    causes={CAUSE_HELP}
                    // Derived from the grid's own column list, so the glossary cannot list a
                    // column the table does not show, or miss one it does.
                    columns={helpFor(Object.keys(getFields()))}
                    onClose={() => setIsHelpOpen(false)}
                />
            )}

            <div className={"sm:flex justify-between"}>
                <ActiveFilters
                    addClass={"mb-2"}
                    filterFields={queryFilterFields}
                    onLabelClick={handleFilterInputChange}
                    persistFilters={[]}
                    onClearFiltersClick={handleClearFiltersClick}
                    translate={translate}
                />

                <div>
                    <Tippy content={translate('text.download_excel')}>
                        <button className="btn-icon" onClick={downloadExcel}>
                            <DocumentArrowDownIcon className="w-6 h-6"/>
                        </button>
                    </Tippy>
                </div>
            </div>

            <TableCard
                className={
                    classNames(
                        "",
                        isTableFullScreen
                            ? "bg-inverse rounded-card fixed inset-1 -top-1 z-50"
                            : "flex flex-col h-dialog-body md:bg-inverse md:border border-tm-gray-300 md:shadow-card"
                    )}
            >
                <div>
                    <TableFilters
                        forceDialog
                        hideLimit
                        filterFields={queryFilterFields}
                        handleInputChange={handleFilterInputChange}
                        translate={translate}
                        onRefreshTable={fetchData}
                        selects={metadata}
                        onFullScreenToggle={handleFullScreenToggle}
                        isTableFullScreen={isTableFullScreen}
                        isLoading={isLoading}
                    />
                </div>

                <ResourceTable
                    tableKey={'ProductServiceID'}
                    data={data}
                    count={count}
                    fields={getFields()}
                    options={tableOptions}
                    isLoading={isLoading}
                    translate={translate}
                    sort={sort}
                    sortBy={sortBy}
                    onSortChange={handleUpdateSort}
                    saveTableOptions={setOptions}
                    maxHeightClass={"max-h-[calc(100vh-28rem)]"}
                    onRowClick={canViewDetails ? handleRowClick : undefined}
                />

                <NoRecordsTable
                    show={(data.length === 0) && !isLoading}
                    title={'Nothing is sitting'}
                    text={'No part has gone unsold for ' + (queryFilterFields.StaleMonths?.value ?? 6) + ' months, and nothing older than ' + (queryFilterFields.MinAgeMonths?.value ?? 3) + ' months has never sold.'}
                    className={"pb-12 pt-16 px-6"}
                />

                <TableCardFooter
                    show={!(!data.length && !isLoading)}
                >
                    <Pagination
                        count={count}
                        isLoading={isLoading}
                        handleQueryChange={handleFilterInputChange}
                        queryFields={queryFilterFields}
                        translate={translate}
                    />
                </TableCardFooter>
            </TableCard>

            <InventoryReportDialog
                isReportDialogOpen={isReportDialogOpen}
                setIsReportDialogOpen={setIsReportDialogOpen}
                selectedReportItem={selectedReportItem}
                dialogResource={dialogResource}
                thirdResource={thirdResource}
                resource={Resources.WorkOrderProductServicesReport}
                dispatch={dispatch}
                translate={translate}
                // Three years, not the calendar year the dialog defaults to. Parts on this
                // report are old by definition, so a narrower window would routinely open
                // the movement ledger on nothing at all.
                startDate={moment().subtract(3, 'years').format('YYYY-MM-DD')}
                endDate={moment().format('YYYY-MM-DD')}
            />
        </React.Fragment>
    )
}
